import { Navigate, Outlet } from 'react-router-dom';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';
import { useColorMode } from '@/theme/ColorModeSelect';
import { canManageTenants } from '@/utils/tenant-detection';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Activity, LogOut, Monitor, Moon, Settings, Sun, User } from 'lucide-react';

export function AdminLayout() {
    const { tenant, isLoading: tenantLoading, error: tenantError } = useTenant();
    const { isAuthenticated, isLoading: authLoading, user, logout } = useAuth();
    const { mode, setMode } = useColorMode();

    // Show loading state while checking authentication and tenant context
    if (authLoading || tenantLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading admin panel...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/auth/login" replace />;
    }

    // Show error state if tenant context failed to load
    if (tenantError) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-600 text-xl mb-4">⚠️</div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Tenant Context Error
                    </h2>
                    <p className="text-gray-600 mb-4">{tenantError}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-sm">Q</span>
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-gray-900">
                                        Quantix Admin
                                    </h1>
                                    <p className="text-xs text-gray-500">Multi-Tenant Management</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            {tenant && (
                                <div className="text-sm text-gray-600">
                                    <span className="font-medium">{tenant.tenantName}</span>
                                </div>
                            )}

                            {/* Queue dashboard (Laravel Horizon), served by the API on this host.
                                The API only lets tenant managers in. */}
                            {canManageTenants(user) && (
                                <Button variant="outline" size="sm" asChild>
                                    <a
                                        href="/api/horizon"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Activity className="h-4 w-4" />
                                        <span className="ml-2">Queues</span>
                                    </a>
                                </Button>
                            )}

                            {/* Theme Mode Selector */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-[120px] justify-between"
                                    >
                                        {mode === 'light' && <Sun className="h-4 w-4" />}
                                        {mode === 'dark' && <Moon className="h-4 w-4" />}
                                        {mode === 'system' && <Monitor className="h-4 w-4" />}
                                        <span className="ml-2">
                                            {mode === 'light'
                                                ? 'Light'
                                                : mode === 'dark'
                                                  ? 'Dark'
                                                  : 'System'}
                                        </span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => setMode('system')}>
                                        <Monitor className="mr-2 h-4 w-4" />
                                        System
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setMode('light')}>
                                        <Sun className="mr-2 h-4 w-4" />
                                        Light
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setMode('dark')}>
                                        <Moon className="mr-2 h-4 w-4" />
                                        Dark
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {/* User Menu */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center space-x-2"
                                    >
                                        <User className="h-4 w-4" />
                                        <span>{user?.name || 'Admin'}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-2 py-1.5 text-sm font-medium">
                                        {user?.name || 'Admin User'}
                                    </div>
                                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                        {user?.email || 'admin@quantix.technology'}
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem>
                                        <Settings className="mr-2 h-4 w-4" />
                                        Settings
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={logout} className="text-red-600">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Outlet />
            </main>

            {/* Footer */}
            <footer className="bg-white border-t mt-auto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                            © 2024 Quantix Admin Platform. All rights reserved.
                        </div>
                        <div className="text-sm text-gray-500">
                            {tenant && <span>Tenant: {tenant.tenantName}</span>}
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
